import { useEffect } from 'react';
import type { ProjectInfo } from 'setup/types/core';
import { getProjectStoreState, useProjectStore } from '../store';
import type { ProjectStore } from '../types';
import { isProjectDataLoading, ProjectDataLoadingFailure } from '../loadingModel';

export function useProject(): ProjectInfo {
  return useProjectStore.use.data().project;
}

export function useProjectId(): string {
  return useProjectStore((state) => state.data.project._id);
}

export function useProjectTimezone(): string {
  return useProjectStore((state) => state.data.project.timezone);
}

export function useProjectOrganizationId(): string {
  return useProjectStore((state) => state.data.project.organizationId);
}

export function useProjectActions(): ProjectStore['actions'] {
  return useProjectStore.use.actions();
}

export function getProjectInfo(): ProjectInfo {
  return getProjectStoreState().data.project;
}

export function getProjectId(): string {
  return getProjectInfo()._id;
}

/**
 * Follows the worker's selected project and, after a page reload, restores the
 * project selected before.
 */
export function useSelectedProjectInitializer(): void {
  const { subscribeToSelectedProjectChanges, restoreSelectedProject } = useProjectActions();

  useEffect(() => subscribeToSelectedProjectChanges(), [subscribeToSelectedProjectChanges]);
  useEffect(() => void restoreSelectedProject(), [restoreSelectedProject]);
}

/** Whether the worker still downloads the selected project's data. */
export function useIsProjectLoading(): boolean {
  return useProjectStore((state) => isProjectDataLoading(state.loading));
}

/** Why the selected project's data could not be loaded; `null` while it loads or once loaded. */
export function useProjectDataLoadingFailure(): ProjectDataLoadingFailure | null {
  return useProjectStore((state) => state.loading.failure);
}
